import { IProgrammeCard, PortalMap, PortalType } from '@studyportals/domain-client';
import { SearchStudyCardInfo, ParameterValue, FilterFacets, SearchResultsFactory } from '.';
import { ParameterType, SortingType } from '../enums';
import { BaseSuperAgentRequestFactory, RequestSender } from '@studyportals/mb-platform-http-requests/base';
import {
	FacetResponseType,
	IProgrammeResult,
	IProgrammeSearchResponse,
	ProgrammeSearchFilters,
	ProgrammeSearchInput,
	SearchApiProgrammeClient
} from '@studyportals/search-api-interface';
import { FacetOptionValue } from '@studyportals/search-filters';
import CompatibilityLayer from '../infrastructure/CompatibilityLayer';
import Tracking from '../infrastructure/tracking/Tracking';
import { DataStorage } from '@studyportals/data-storage';

export default class StudySearchClient {
	private readonly searchResultsFactory: SearchResultsFactory;
	private readonly searchApiProgrammeClient: SearchApiProgrammeClient;

	private readonly SIZE_DEFAULT = 10;
	private readonly START_DEFAULT = 0;
	private readonly SORTING_DEFAULT = SortingType.RELEVANCE;
	private readonly requestFactory: BaseSuperAgentRequestFactory = new BaseSuperAgentRequestFactory();
	private readonly requestSender: RequestSender = new RequestSender();
	public constructor(
		private readonly baseQueryUrl: string = '',
		private readonly euBaseQueryUrl: string = '',
		private readonly portalMap: PortalMap
	) {
		this.searchResultsFactory = new SearchResultsFactory();
		this.searchApiProgrammeClient = new SearchApiProgrammeClient(
			this.retrieveBaseUrl(),
			this.requestFactory,
			this.requestSender,
			this.portalMap
		);
	}

	private retrieveBaseUrl(): string {
		if (this.isExperiment()) {
			return this.baseQueryUrl;
		}
		return this.euBaseQueryUrl;
	}

	private isExperiment(): boolean {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const isExperiment = DataStorage.retrieve('Experiment/multiregion_search');
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		return isExperiment?.number === 1;
	}

	public async retrieveSearchResults(
		parameters: ParameterValue[],
		portalType: PortalType,
		size: number = this.SIZE_DEFAULT,
		start: number = this.START_DEFAULT,
		sorting: SortingType = this.SORTING_DEFAULT,
		facetsToRequest?: FacetOptionValue[]
	): Promise<{ results: SearchStudyCardInfo[]; facets: FilterFacets[] | undefined; count: number }> {
		const searchInput = CompatibilityLayer.mapToProgrammeSearchInput(parameters, sorting, portalType, facetsToRequest);
		const result = await this.timeResponse(searchInput, size, start);
		const facets = result.facets;

		return {
			results: this.searchResultsFactory.reconstituteSearchStudyCards([...result.programmes]),
			facets: facets ? CompatibilityLayer.mapFacetsToFilterFacets([...result.facets]) : undefined,
			count: this.countResults(facets, portalType, parameters)
		};
	}

	private async timeResponse(
		searchInput: ProgrammeSearchInput,
		size: number,
		start: number
	): Promise<IProgrammeSearchResponse<IProgrammeResult<IProgrammeCard>>> {
		const startTime = Date.now();

		const result = await this.searchApiProgrammeClient.searchCards(
			new ProgrammeSearchFilters().from({ ...searchInput }),
			size,
			start
		);
		const timeTaken = Date.now() - startTime;

		const experimentVariation = this.isExperiment() ? 'experiment_1' : 'control_0';
		Tracking.trackResponseTime(timeTaken, experimentVariation);

		return result;
	}

	private countResults(
		facets: readonly FacetResponseType[] | undefined,
		portalType: PortalType,
		parameters: ParameterValue[]
	): number {
		if (!facets) {
			return 0;
		}
		const filterFacets = facets.find((_) => _.filter === FacetOptionValue.EDUCATIONAL_LEVEL);
		if (!filterFacets) {
			return 0;
		}

		if (portalType === PortalType.DISTANCE_LEARNING) {
			const selectedLevels = parameters
				.filter((p) => p.type === ParameterType.EDUCATION_LEVEL)
				.map((p) => p.value)
				.flat();
			if (selectedLevels.length === 0) {
				const values = filterFacets.values.map((_) => _.count);
				if (values.length === 0) {
					return 0;
				}

				return values.reduce((current, next) => current + next);
			}

			const levelFacetValues = filterFacets.values.filter((_) => selectedLevels.includes(_.value));
			if (levelFacetValues.length === 0) {
				return 0;
			}

			return levelFacetValues.map((_) => _.count).reduce((current, next) => current + next);
		}

		const facetValue = filterFacets.values.find((_) => (_.value as PortalType) === portalType);
		if (facetValue !== undefined) {
			return facetValue.count;
		}

		return 0;
	}
}
